import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { LoginPayload, LoginSuccessPayload } from '../../types/auth';
import { validateEmail, validatePassword } from '../../utils/validation';
import authService from '../../services/auth/authService';
import smsService, { SmsError } from '../../services/sms';
import { Button } from '../../components/ui/shadcn/button';
import { Input } from '../../components/ui/shadcn/input';
import { Label } from '../../components/ui/shadcn/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/shadcn/card';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { safeReturnLocation } from '../../utils/safeReturnLocation';

// challenge 세션이 무효(누락·만료·재사용)임을 뜻하는 서버 error.code — 로그인 폼 복귀 트리거 [Q2-6][C-20]
const CHALLENGE_RESET_CODES = ['SMS_CHALLENGE_REQUIRED', 'SMS_CHALLENGE_INVALID', 'CHALLENGE_USED'];

interface LocationState {
  from?: unknown; // ProtectedRoute 가 넘기는 location — safeReturnLocation 이 검증한다
}

const LoginPageNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSmsAuthSuccess } = useAuth();
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  const [formData, setFormData] = useState<LoginPayload>({
    email: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  // 로그인 6시간 재인증 서명 챌린지 — SMS send/verify 에 전달 (user_id 미전송) [C-16][C-20]
  const [smsChallenge, setSmsChallenge] = useState<string | null>(null);

  /**
   * 튕겨 나온 이유 [퇴사자계정차단 S2]
   *
   * 퇴사 처리된 계정은 어느 화면에 있든 로그인 화면으로 보내진다.
   * 이유를 함께 보여 주지 않으면 «갑자기 로그아웃됐다» 로만 느끼고,
   * 다시 로그인 → 또 튕김 을 반복하게 된다.
   * apiClient 가 sessionStorage 에 남긴 사유를 **1회만** 읽고 지운다.
   */
  useEffect(() => {
    try {
      const reason = window.sessionStorage.getItem('auth_blocked_reason');
      if (reason) {
        window.sessionStorage.removeItem('auth_blocked_reason');
        showErrorToast(reason);
      }
    } catch {
      /* 세션 저장소를 못 읽어도 로그인 자체는 되어야 한다 */
    }
    // 화면 진입 시 1회만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SMS verification states
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // 원래 주소(검색어·해시 포함 — 엑셀 클레임 바로가기의 ?id=&openDetail=1) 로 복귀. 같은 사이트 주소만
  const from = safeReturnLocation((location.state as LocationState | null)?.from);

  // Countdown timer
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    const emailValidation = validateEmail(formData.email);
    if (emailValidation.hasError) {
      newErrors.email = emailValidation.message;
      isValid = false;
    }

    const passwordValidation = validatePassword(formData.password);
    if (passwordValidation.hasError) {
      newErrors.password = passwordValidation.message;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response: any = await authService.login(formData);

      // Check if phone verification is required
      if (response.success && response.phone_verification_required) {
        setPendingUser(response.user);
        // 서버 발급 챌린지 저장 — 이후 SMS send/verify 는 이 챌린지로 userId 확정 [C-16][C-20]
        setSmsChallenge(response.smsChallenge || null);
        setShowPhoneVerification(true);
        setIsLoading(false);
        showSuccessToast('휴대폰 인증이 필요합니다.');
        return;
      }

      // Normal login success
      if (response.success && response.token) {
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
        showSuccessToast('로그인 성공!');
        navigate(from, { replace: true });
      } else {
        throw new Error(response.message || '로그인에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      showErrorToast(
        error?.response?.data?.message || error.message || '로그인 중 오류가 발생했습니다.'
      );
      setIsLoading(false);
    }
  };

  // 인증 화면 상태를 전부 비우고 로그인 폼으로 복귀 (challenge 무효 시) [Q2-6]
  const resetToLoginForm = (message?: string) => {
    setShowPhoneVerification(false);
    setPendingUser(null);
    setSmsChallenge(null);
    setVerificationCode('');
    setCodeSent(false);
    setCountdown(0);
    setIsSending(false);
    setIsVerifying(false);
    showErrorToast(message || '인증 세션이 만료되었습니다. 다시 로그인해주세요.');
  };

  // Send SMS verification code
  const handleSendCode = async () => {
    if (!pendingUser || !pendingUser.phone_number) {
      showErrorToast('전화번호 정보가 없습니다.');
      return;
    }
    if (!smsChallenge) {
      // 로컬에 challenge 가 없으면 이미 세션이 소실된 것 — 로그인 폼으로 복귀 [Q2-6]
      resetToLoginForm('인증 세션이 만료되었습니다. 다시 로그인해주세요.');
      return;
    }

    setIsSending(true);

    try {
      // user_id 미전송 — 챌린지로만 userId 확정 [C-16][C-20]
      const response = await smsService.sendVerificationCode(pendingUser.phone_number, { smsChallenge });

      if (response.success) {
        setCodeSent(true);
        setCountdown(300); // 5 minutes
        setVerificationCode(''); // 재전송 시 이전 입력 코드 초기화 [Q2-7]
        showSuccessToast('인증번호가 발송되었습니다.');
      } else {
        showErrorToast(response.error?.message || '인증번호 발송에 실패했습니다.');
      }
    } catch (error: any) {
      // challenge 누락/만료/재사용 → 로그인 폼 복귀 [Q2-6]
      if (CHALLENGE_RESET_CODES.includes((error as SmsError)?.code || '')) {
        resetToLoginForm(error.message);
      } else {
        showErrorToast(error.message || '인증번호 발송에 실패했습니다.');
      }
    } finally {
      setIsSending(false);
    }
  };

  // Verify SMS code
  const handleVerifyCode = async () => {
    if (!pendingUser || !pendingUser.phone_number) {
      showErrorToast('전화번호 정보가 없습니다.');
      return;
    }

    if (!verificationCode) {
      showErrorToast('인증번호를 입력해주세요.');
      return;
    }
    if (!smsChallenge) {
      // 로컬에 challenge 가 없으면 세션 소실 — 로그인 폼 복귀 [Q2-6]
      resetToLoginForm('인증 세션이 만료되었습니다. 다시 로그인해주세요.');
      return;
    }

    setIsVerifying(true);

    try {
      // user_id 미전송 — 챌린지로만 userId 확정 [C-16][C-20]
      const response = await smsService.verifyCode(pendingUser.phone_number, verificationCode, { smsChallenge });

      if (response.success) {
        // ✅ SMS 인증 응답에 JWT 토큰이 포함되어 있으면 AuthContext 표준 경로로 저장 [Q2-8]
        if (response.token && response.user) {
          // authService/AuthContext 표준(user_info + session_expiry) 재사용 — 중복 구현 제거
          setSmsAuthSuccess(response as unknown as LoginSuccessPayload);

          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          }

          // 인증 화면 상태 정리
          setShowPhoneVerification(false);
          setPendingUser(null);
          setSmsChallenge(null);
          setVerificationCode('');
          setCodeSent(false);
          setCountdown(0);

          showSuccessToast('로그인 성공!');
          navigate(from, { replace: true });
        } else {
          // 토큰이 없는 경우 (예상치 못한 상황)
          setIsVerifying(false);
          showErrorToast('인증 정보를 받지 못했습니다. 다시 시도해주세요.');
        }
      } else {
        showErrorToast(response.error?.message || '인증번호가 일치하지 않습니다.');
        setIsVerifying(false);
      }
    } catch (error: any) {
      // challenge 재사용/만료(CHALLENGE_USED / SMS_CHALLENGE_*) → 로그인 폼 복귀,
      // 그 외(코드 오류 등)는 인증 화면 유지 [Q2-6]
      if (CHALLENGE_RESET_CODES.includes((error as SmsError)?.code || '')) {
        resetToLoginForm(error.message);
      } else {
        showErrorToast(error.message || '인증 처리에 실패했습니다.');
        setIsVerifying(false);
      }
    }
  };

  if (showPhoneVerification && pendingUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">휴대폰 인증</CardTitle>
            <CardDescription className="text-center">
              로그인을 완료하려면 휴대폰 인증이 필요합니다
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>전화번호</Label>
              <Input
                value={pendingUser.phone_number || ''}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="verification_code">인증번호</Label>
              <div className="flex space-x-2">
                <Input
                  id="verification_code"
                  type="text"
                  placeholder="인증번호 6자리"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  maxLength={6}
                  disabled={!codeSent || isVerifying}
                />
                <Button
                  type="button"
                  onClick={codeSent ? handleSendCode : handleSendCode}
                  disabled={isSending || (codeSent && countdown > 0)}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : codeSent && countdown > 0 ? (
                    `${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`
                  ) : (
                    codeSent ? '재전송' : '인증번호 발송'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              onClick={handleVerifyCode}
              disabled={!codeSent || !verificationCode || isVerifying}
              className="w-full"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  인증 중...
                </>
              ) : (
                '인증 완료'
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowPhoneVerification(false);
                setPendingUser(null);
                setSmsChallenge(null);
                setVerificationCode('');
                setCodeSent(false);
                setCountdown(0);
              }}
              className="w-full"
            >
              취소
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">AI ERP 시스템</CardTitle>
          <CardDescription className="text-center">
            계정에 로그인하여 시작하세요
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">이메일</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'border-red-500' : ''}
                disabled={isLoading}
                required
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">비밀번호</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="비밀번호 입력"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password}</p>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                  로그인 상태 유지
                </Label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                비밀번호 찾기
              </Link>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  로그인 중...
                </>
              ) : (
                '로그인'
              )}
            </Button>
            <div className="text-center text-sm text-gray-600">
              계정이 없으신가요?{' '}
              <Link to="/register" className="text-primary hover:underline">
                회원가입
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default LoginPageNew;
